<template>
  <div class="neutral-court-picker">
    <!-- Booked summary (locked neutral ground) -->
    <div v-if="booked" class="ncp-booked">
      <p class="ncp-booked__title">
        <i class="fa-solid fa-circle-check" aria-hidden="true" />
        Neutral ground booked
      </p>
      <p class="ncp-booked__meta">
        <strong>{{ booked.court.pitch_name }}</strong>
        <span v-if="booked.booking?.booking_number"> · Booking #{{ booked.booking.booking_number }}</span>
        <br />
        <span v-if="booked.booking?.date">
          {{ formatBookingSlot(booked.booking) }}
        </span>
        <span v-if="booked.court.zone || booked.court.resident_county">
          <br />
          {{ [booked.court.zone, booked.court.resident_county].filter(Boolean).join(", ") }}
        </span>
      </p>
      <div class="ncp-booked__actions">
        <a
          v-if="booked.court.owner_phone"
          :href="`tel:${sanitizePhone(booked.court.owner_phone)}`"
          class="ncp-btn ncp-btn--call"
        >
          <i class="fa-solid fa-phone" aria-hidden="true" />
          Call facility
        </a>
      </div>
    </div>

    <div v-else class="ncp-trigger">
      <button type="button" class="ncp-btn ncp-btn--primary" @click="openBrowseModal">
        <i :class="facilityIcon" aria-hidden="true" />
        Browse &amp; book a neutral {{ facilityNoun }}
      </button>
      <p v-if="required && !booked" class="ncp-inline-hint">
        Select and book a court for neutral ground before sending the challenge.
      </p>
    </div>

    <!-- Browse courts modal -->
    <transition name="modal">
      <div
        v-if="showBrowseModal"
        class="ncp-backdrop"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ncp-browse-title"
        @click.self="closeBrowseModal"
      >
        <div class="ncp-modal" @click.stop>
          <div class="ncp-modal__header">
            <div>
              <h3 id="ncp-browse-title">Choose a neutral {{ facilityNoun }}</h3>
              <p>
                {{ facilityTypeLabel }} facilities only — filter by county and constituency, then book a slot.
              </p>
            </div>
            <button type="button" class="ncp-modal__close" aria-label="Close" @click="closeBrowseModal">
              <i class="fa-solid fa-times" />
            </button>
          </div>

          <p class="ncp-notice">
            <i class="fa-solid fa-phone-volume" aria-hidden="true" />
            <span>
              <strong>Call the facility first</strong> to confirm real availability before you tap
              <strong>Book</strong>. Unverified slots can put your match at risk of forfeit if the
              court is double-booked or closed.
            </span>
          </p>

          <div class="ncp-filters">
            <div>
              <label for="ncp-county">County</label>
              <select id="ncp-county" v-model="filters.county" @change="onCountyChange">
                <option value="">All counties</option>
                <option v-for="c in availableCounties" :key="c" :value="c">{{ c }}</option>
              </select>
            </div>
            <div>
              <label for="ncp-constituency">Constituency</label>
              <select
                id="ncp-constituency"
                v-model="filters.constituency"
                :disabled="!filters.county"
                @change="fetchCourts"
              >
                <option value="">
                  {{ filters.county ? "All constituencies" : "Select county first" }}
                </option>
                <option v-for="z in availableConstituencies" :key="z" :value="z">{{ z }}</option>
              </select>
            </div>
            <div>
              <label for="ncp-search">Search by name</label>
              <input
                id="ncp-search"
                v-model.trim="filters.search"
                type="search"
                placeholder="Court or gym name"
                @input="debouncedFetch"
              />
            </div>
          </div>

          <div class="ncp-modal__body">
            <div v-if="loadingCourts" class="ncp-loading">
              <i class="fa-solid fa-spinner fa-spin" aria-hidden="true" />
              <p>Loading {{ facilityTypeLabel }} {{ facilityNounPlural }}…</p>
            </div>
            <div v-else-if="!courts.length" class="ncp-empty">
              <p>No {{ facilityNounPlural }} match your filters. Try another county or clear the search.</p>
            </div>
            <div v-else class="ncp-grid">
              <article v-for="court in courts" :key="court.id" class="ncp-card">
                <div class="ncp-card__head">
                  <h4 class="ncp-card__name">{{ court.pitch_name }}</h4>
                  <span
                    class="ncp-badge"
                    :class="court.is_available ? 'ncp-badge--available' : 'ncp-badge--unavailable'"
                  >
                    {{ court.is_available ? "Listed available" : "Unavailable" }}
                  </span>
                </div>
                <p v-if="court.zone || court.resident_county" class="ncp-card__meta">
                  <span class="ncp-card__row">
                    <i class="fa-solid fa-map-marker-alt" aria-hidden="true" />
                    {{ [court.zone, court.resident_county].filter(Boolean).join(", ") }}
                  </span>
                </p>
                <p v-if="court.owner_phone" class="ncp-card__meta">
                  <span class="ncp-card__row">
                    <i class="fa-solid fa-phone" aria-hidden="true" />
                    <a :href="`tel:${sanitizePhone(court.owner_phone)}`">{{ court.owner_phone }}</a>
                  </span>
                </p>
                <p class="ncp-card__charges">
                  KSh {{ formatPrice(court.hourly_rate) }} / hour
                </p>
                <div class="ncp-card__actions">
                  <a
                    v-if="court.owner_phone"
                    :href="`tel:${sanitizePhone(court.owner_phone)}`"
                    class="ncp-btn ncp-btn--call"
                    @click.stop
                  >
                    <i class="fa-solid fa-phone" />
                    Call
                  </a>
                  <button
                    type="button"
                    class="ncp-btn ncp-btn--primary"
                    :disabled="!court.is_available"
                    @click="startBook(court)"
                  >
                    Book
                  </button>
                </div>
              </article>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <!-- Confirm booking modal -->
    <transition name="modal">
      <div
        v-if="showConfirmModal && pendingCourt"
        class="ncp-backdrop"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ncp-confirm-title"
        @click.self="closeConfirmModal"
      >
        <div class="ncp-modal ncp-modal--confirm" @click.stop>
          <div class="ncp-modal__header">
            <div>
              <h3 id="ncp-confirm-title">Confirm court booking</h3>
              <p>Set the date and time for <strong>{{ pendingCourt.pitch_name }}</strong>.</p>
            </div>
            <button type="button" class="ncp-modal__close" aria-label="Close" @click="closeConfirmModal">
              <i class="fa-solid fa-times" />
            </button>
          </div>

          <p class="ncp-notice">
            <i class="fa-solid fa-circle-info" aria-hidden="true" />
            <span>
              Only confirm if you have already called
              <strong v-if="pendingCourt.owner_phone">{{ pendingCourt.owner_phone }}</strong>
              <strong v-else>the facility</strong>
              and verified the slot is free.
            </span>
          </p>

          <div class="ncp-confirm-form">
            <div class="ncp-confirm-summary">
              <div><strong>Court:</strong> {{ pendingCourt.pitch_name }}</div>
              <div><strong>Charges:</strong> KSh {{ formatPrice(pendingCourt.hourly_rate) }} / hour</div>
              <div v-if="estimatedTotal > 0">
                <strong>Estimated total:</strong> KSh {{ formatPrice(estimatedTotal) }}
                <span class="ncp-billing-note"> (minimum 1 hour billed)</span>
              </div>
            </div>

            <div class="form-group">
              <label for="ncp-book-date">Booking date *</label>
              <input id="ncp-book-date" v-model="bookingForm.date" type="date" :min="minDate" />
            </div>
            <div class="form-row" style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px">
              <div class="form-group">
                <label for="ncp-start">Start time *</label>
                <input id="ncp-start" v-model="bookingForm.startTime" type="time" />
              </div>
              <div class="form-group">
                <label for="ncp-end">End time *</label>
                <input id="ncp-end" v-model="bookingForm.endTime" type="time" />
              </div>
            </div>

            <p v-if="bookingError" class="ncp-error" role="alert">{{ bookingError }}</p>

            <div class="ncp-confirm-actions">
              <button type="button" class="ncp-btn" :disabled="submitting" @click="closeConfirmModal">
                Cancel
              </button>
              <button
                type="button"
                class="ncp-btn ncp-btn--primary"
                :disabled="submitting || !isBookingFormValid"
                @click="confirmBooking"
              >
                <i v-if="submitting" class="fa-solid fa-spinner fa-spin" aria-hidden="true" />
                <template v-else>Confirm booking</template>
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { buildApiUrl } from "@/config/api";
import { kenyaCounties } from "@/routes/match_hub/football/counties.js";
import {
  facilityAcceptsBookingForSport,
  facilityTypeIcon,
  facilityTypeNoun,
  formatFacilityType,
  normalizeMatchSport,
  resolvePitchTypeForSport,
} from "@/utils/facilityTypes";
import "./neutralCourtPicker.css";

/** Facilities are billed in whole hours; match length may be shorter. */
const MIN_BOOKING_MINUTES = 60;
const MIN_BOOKING_HOURS = 1;

function storageKeyForSport(sport) {
  const slug = normalizeMatchSport(sport) || "sport";
  return `toafrica_${slug}_neutral_court_booking`;
}

export default {
  name: "NeutralCourtPicker",

  props: {
    facilityType: {
      type: String,
      default: "basketball",
    },
    booked: {
      type: Object,
      default: null,
    },
    locked: {
      type: Boolean,
      default: false,
    },
    required: {
      type: Boolean,
      default: true,
    },
    autoOpenBrowse: {
      type: Boolean,
      default: false,
    },
    suggestedDate: {
      type: String,
      default: "",
    },
    suggestedStartTime: {
      type: String,
      default: "",
    },
    suggestedDurationMinutes: {
      type: Number,
      default: 60,
    },
    challengerTeamId: {
      type: [Number, String],
      default: null,
    },
    opponentTeamId: {
      type: [Number, String],
      default: null,
    },
  },

  emits: ["update:booked", "booked", "cleared", "browse-closed"],

  data() {
    return {
      showBrowseModal: false,
      showConfirmModal: false,
      loadingCourts: false,
      courts: [],
      pendingCourt: null,
      submitting: false,
      bookingError: "",
      fetchTimer: null,
      filters: {
        county: "",
        constituency: "",
        search: "",
      },
      availableCounties: Object.keys(kenyaCounties),
      availableConstituencies: [],
      bookingForm: {
        date: "",
        startTime: "09:00",
        endTime: "10:00",
      },
    };
  },

  computed: {
    normalizedFacilityType() {
      return normalizeMatchSport(this.facilityType);
    },
    facilityTypeLabel() {
      return formatFacilityType(this.normalizedFacilityType);
    },
    facilityIcon() {
      return facilityTypeIcon(this.normalizedFacilityType);
    },
    facilityNoun() {
      return facilityTypeNoun(this.normalizedFacilityType);
    },
    facilityNounPlural() {
      const n = this.facilityNoun;
      if (n === "pitch") return "pitches";
      if (n.endsWith("s")) return `${n}es`;
      return `${n}s`;
    },
    storageKey() {
      return storageKeyForSport(this.normalizedFacilityType);
    },
    minDate() {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return today.toISOString().split("T")[0];
    },

    isBookingFormValid() {
      if (!this.bookingForm.date || !this.bookingForm.startTime || !this.bookingForm.endTime) {
        return false;
      }
      return this.bookingForm.startTime < this.bookingForm.endTime;
    },

    estimatedTotal() {
      if (!this.pendingCourt?.hourly_rate) return 0;
      const hours = this.billingHoursDecimal();
      return Math.round(hours * this.pendingCourt.hourly_rate * 100) / 100;
    },
    effectiveSuggestedDurationMinutes() {
      const n = Number(this.suggestedDurationMinutes);
      if (!Number.isFinite(n) || n <= 0) return MIN_BOOKING_MINUTES;
      return Math.max(Math.round(n), MIN_BOOKING_MINUTES);
    },
  },

  watch: {
    autoOpenBrowse: {
      immediate: true,
      handler(open) {
        if (open && !this.booked) {
          this.openBrowseModal();
        }
      },
    },
    suggestedDate(val) {
      if (val && !this.bookingForm.date) this.bookingForm.date = val;
    },
    suggestedStartTime(val) {
      if (val) this.applySuggestedTimes(val);
    },
    suggestedDurationMinutes() {
      if (this.bookingForm.startTime) {
        this.bookingForm.endTime = this.endTimeFromStart(
          this.bookingForm.startTime,
          this.effectiveSuggestedDurationMinutes,
        );
      }
    },
  },

  mounted() {
    this.initBookingForm();
  },

  methods: {
    initBookingForm() {
      if (this.suggestedDate) {
        this.bookingForm.date = this.suggestedDate;
      } else {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        this.bookingForm.date = tomorrow.toISOString().split("T")[0];
      }
      if (this.suggestedStartTime) {
        this.applySuggestedTimes(this.suggestedStartTime);
      }
    },

    applySuggestedTimes(startTime) {
      this.bookingForm.startTime = startTime;
      this.bookingForm.endTime = this.endTimeFromStart(
        startTime,
        this.effectiveSuggestedDurationMinutes,
      );
    },

    endTimeFromStart(startTime, durationMinutes) {
      const [h, m = "0"] = String(startTime).split(":");
      const startM = parseInt(h, 10) * 60 + parseInt(m, 10);
      const endM = Math.min(startM + (durationMinutes || 60), 23 * 60 + 59);
      const eh = Math.floor(endM / 60);
      const em = endM % 60;
      return `${String(eh).padStart(2, "0")}:${String(em).padStart(2, "0")}`;
    },

    durationHoursDecimal() {
      if (!this.bookingForm.startTime || !this.bookingForm.endTime) return 0;
      const [sh, sm = "0"] = this.bookingForm.startTime.split(":");
      const [eh, em = "0"] = this.bookingForm.endTime.split(":");
      const startM = parseInt(sh, 10) * 60 + parseInt(sm, 10);
      const endM = parseInt(eh, 10) * 60 + parseInt(em, 10);
      return Math.max(0, (endM - startM) / 60);
    },

    billingHoursDecimal() {
      return Math.max(this.durationHoursDecimal(), MIN_BOOKING_HOURS);
    },

    ensureMinimumBookingWindow() {
      if (!this.bookingForm.startTime) return;
      if (this.durationHoursDecimal() >= MIN_BOOKING_HOURS) return;
      this.bookingForm.endTime = this.endTimeFromStart(
        this.bookingForm.startTime,
        MIN_BOOKING_MINUTES,
      );
    },

    sanitizePhone(phone) {
      return String(phone || "").replace(/\s+/g, "");
    },

    formatPrice(amount) {
      const n = Number(amount);
      if (!Number.isFinite(n)) return "0";
      return n.toLocaleString("en-KE");
    },

    formatBookingSlot(booking) {
      if (!booking?.date) return "";
      const start = booking.start_time?.slice(0, 5) || booking.start_time;
      const end = booking.end_time?.slice(0, 5) || booking.end_time;
      return `${booking.date}${start ? ` · ${start}` : ""}${end ? `–${end}` : ""}`;
    },

    onCountyChange() {
      this.filters.constituency = "";
      this.availableConstituencies = kenyaCounties[this.filters.county] || [];
      this.fetchCourts();
    },

    debouncedFetch() {
      clearTimeout(this.fetchTimer);
      this.fetchTimer = setTimeout(() => this.fetchCourts(), 300);
    },

    openBrowseModal() {
      this.showBrowseModal = true;
      this.fetchCourts();
    },

    closeBrowseModal() {
      this.showBrowseModal = false;
      this.$emit("browse-closed");
    },

    closeConfirmModal() {
      this.showConfirmModal = false;
      this.pendingCourt = null;
      this.bookingError = "";
    },

    async fetchCourts() {
      if (!this.normalizedFacilityType) {
        this.courts = [];
        return;
      }

      this.loadingCourts = true;
      try {
        const params = new URLSearchParams();
        params.append("type_filter", this.normalizedFacilityType);
        params.append("limit", "100");
        if (this.filters.search) params.append("search", this.filters.search);
        if (this.filters.county) params.append("county_filter", this.filters.county);
        if (this.filters.constituency) params.append("zone_filter", this.filters.constituency);

        const response = await fetch(
          buildApiUrl(`/api/facilities/all?${params.toString()}`),
          { headers: { "Content-Type": "application/json" } },
        );

        if (!response.ok) {
          throw new Error(`Could not load ${this.facilityNounPlural} (${response.status})`);
        }

        const result = await response.json();
        const list = result.facilities || result.pitches;
        const raw = Array.isArray(list) ? list : [];
        this.courts = raw.filter((court) =>
          facilityAcceptsBookingForSport(court.pitch_type, this.normalizedFacilityType),
        );
      } catch (err) {
        console.error("Neutral court fetch failed:", err);
        this.courts = [];
      } finally {
        this.loadingCourts = false;
      }
    },

    async fetchCourtDetail(courtId) {
      const response = await fetch(buildApiUrl(`/api/facilities/${courtId}`));
      if (!response.ok) return null;
      return response.json();
    },

    assertCourtMatchesSport(court) {
      if (!court) return false;
      if (facilityAcceptsBookingForSport(court.pitch_type, this.normalizedFacilityType)) {
        return true;
      }
      this.bookingError = `This facility is listed as ${formatFacilityType(court.pitch_type) || "another sport"} and cannot be used for ${this.facilityTypeLabel} matches.`;
      return false;
    },

    async startBook(court) {
      this.bookingError = "";
      if (!this.assertCourtMatchesSport(court)) return;

      let enriched = { ...court };
      try {
        const detail = await this.fetchCourtDetail(court.id);
        if (detail) {
          enriched = {
            ...enriched,
            pin_location: detail.pin_location || enriched.pin_location || null,
            address: detail.address || enriched.address,
            owner_phone: detail.owner_phone || detail.phone || enriched.owner_phone,
          };
        }
      } catch (e) {
        console.warn("Court detail fetch failed:", e);
      }
      this.pendingCourt = enriched;
      this.initBookingForm();
      this.showBrowseModal = false;
      this.showConfirmModal = true;
    },

    getAuthToken() {
      return (
        localStorage.getItem("access_token") ||
        localStorage.getItem("token") ||
        sessionStorage.getItem("access_token") ||
        sessionStorage.getItem("token")
      );
    },

    async parseBookingError(response) {
      const text = await response.text();
      try {
        const body = text ? JSON.parse(text) : null;
        const detail = body?.detail;
        if (typeof detail === "string") return detail;
        if (detail?.message) return detail.message;
        if (Array.isArray(detail)) {
          return detail.map((d) => d.msg || d.message).filter(Boolean).join(" ");
        }
        return body?.message || text || response.statusText;
      } catch {
        return text || response.statusText;
      }
    },

    bookingNotesForSport() {
      const label = this.facilityTypeLabel || "Match";
      return `${label} match — neutral ground booking via Match Hub`;
    },

    async confirmBooking() {
      if (!this.isBookingFormValid || !this.pendingCourt) return;
      if (!this.assertCourtMatchesSport(this.pendingCourt)) return;

      const token = this.getAuthToken();
      if (!token) {
        this.bookingError = "Sign in as a customer to book a neutral court.";
        return;
      }

      this.submitting = true;
      this.bookingError = "";

      this.ensureMinimumBookingWindow();
      const hours = this.billingHoursDecimal();
      const totalCost =
        Math.round(hours * (this.pendingCourt.hourly_rate || 0) * 100) / 100;

      const payload = {
        pitch_id: parseInt(String(this.pendingCourt.id), 10),
        date: this.bookingForm.date,
        start_time: this.bookingForm.startTime,
        end_time: this.bookingForm.endTime,
        total_cost: totalCost,
        notes: this.bookingNotesForSport(),
        booking_purpose: "neutral_ground",
        challenger_team_id: this.challengerTeamId ? Number(this.challengerTeamId) : null,
        opponent_team_id: this.opponentTeamId ? Number(this.opponentTeamId) : null,
      };

      try {
        const sportKey = this.normalizedFacilityType;
        const bookingPathMap = {
          football: "/api/bookings/",
          basketball: "/api/bookings/basketball/",
          rugby: "/api/bookings/rugby/",
          volleyball: "/api/bookings/volleyball/",
          tennis: "/api/bookings/tennis/",
        };
        const bookingPath = bookingPathMap[sportKey] || "/api/bookings/";
        const response = await fetch(buildApiUrl(bookingPath), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          const message = await this.parseBookingError(response);
          throw new Error(message || `Booking failed (${response.status})`);
        }

        let data = null;
        try {
          data = await response.json();
        } catch {
          data = null;
        }

        const bookedPayload = {
          court: {
            ...this.pendingCourt,
            pitch_name: data?.pitch_name || this.pendingCourt.pitch_name,
            pitch_type: resolvePitchTypeForSport(
              data?.pitch_type || this.pendingCourt.pitch_type,
              this.normalizedFacilityType,
            ),
            address: data?.pitch_address || this.pendingCourt.address,
            zone: data?.pitch_zone || this.pendingCourt.zone,
            resident_county: data?.pitch_county || this.pendingCourt.resident_county,
            pin_location: data?.pitch_pin_location || this.pendingCourt.pin_location,
            owner_phone: data?.owner_phone || this.pendingCourt.owner_phone,
          },
          booking: {
            pitch_id: data?.pitch_id ?? this.pendingCourt.id,
            date: data?.date || this.bookingForm.date,
            start_time: data?.start_time || this.bookingForm.startTime,
            end_time: data?.end_time || this.bookingForm.endTime,
            total_cost: data?.total_cost ?? totalCost,
            booking_number: data?.booking_number ?? null,
            booking_purpose: data?.booking_purpose || "neutral_ground",
          },
        };

        this.persistBooking(bookedPayload);
        this.$emit("update:booked", bookedPayload);
        this.$emit("booked", bookedPayload);
        this.closeConfirmModal();
      } catch (err) {
        console.error("Neutral court booking failed:", err);
        this.bookingError = err.message || "Could not complete booking. Try again.";
      } finally {
        this.submitting = false;
      }
    },

    persistBooking(payload) {
      try {
        if (payload) {
          sessionStorage.setItem(this.storageKey, JSON.stringify(payload));
        } else {
          sessionStorage.removeItem(this.storageKey);
        }
      } catch {
        /* ignore quota */
      }
    },
  },
};
</script>

<style scoped>
.ncp-inline-hint {
  margin: 8px 0 0;
  font-size: 0.82rem;
  color: var(--text-color2);
}

.ncp-billing-note {
  font-size: 0.85em;
  font-weight: 500;
  color: var(--text-color2);
}
</style>
